import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, delay, distinctUntilChanged, map, Observable } from 'rxjs';

import { Faction, UnitOption, UnitTemplate } from '../forces/models';
import { HttpRequestConfig, RestDataService } from './restDataService/restDataService';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { entityDataSelector } from './state/entity-reducer';
import { UserService } from './userService/user-service';

@Injectable({ providedIn: 'root' })
export abstract class DataLibrary {
    abstract factions$: Observable<Faction[]>;
    abstract unitTemplates$: Observable<UnitTemplate[]>;
    abstract options$: Observable<UnitOption[]>;
    abstract gameId: string;

    // `abs-page-layout` (the only consumer of ready$) hides ALL of a page's projected content
    // until this is true — including pages that never touch entity data at all, like a public
    // event's own details. init() below only calls initGame() once logged in, so for an
    // anonymous visitor `s.data[gameId]` never appears and this would otherwise stay false
    // forever, permanently blanking any per-game page reachable without login (today, events
    // — the only per-game route with no UserGuard). Not logged in means nothing is ever going
    // to load, which is a different thing from "still loading" — so it counts as ready, not stuck.
    ready$ = combineLatest([this.store.select(entityDataSelector), this.userService.loggedIn$]).pipe(
        map(([s, loggedIn]) => {
            if (!this.gameId || !loggedIn) {
                return true;
            }

            const gameData = s.data[this.gameId];
            if (!gameData) {
                return false;
            }
            const values = Object.values(gameData);
            const ready = !values.some((v) => v.status !== 'LOADED');
            return ready;
        }),
        delay(0)
    );

    init(opts?: HttpRequestConfig) {
        this.userService.loggedIn$.pipe(distinctUntilChanged()).subscribe((loggedIn) => {
            if (loggedIn) {
                this.initGame(opts);
            }
        });
    }
    abstract initGame(opts: HttpRequestConfig): void;

    constructor(
        protected restDataService: RestDataService,
        protected store: Store,
        protected route: ActivatedRoute,
        protected userService: UserService
    ) {}
}
